import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ApiService } from 'src/app/api.service';
import { ThemingService } from 'src/app/theming.service';
import { LambdaService } from 'src/app/lambda.service';
import {
  OPTION_TYPE_MAP,
  isOptionType,
  isOptionValid,
  isValidHttpUrl,
  getOptionTypeLabel,
  createBlankOption,
  createBlankSubOption,
} from '../shared/menu-form.helpers';

declare var $: any;

@Component({
  selector: 'app-create-menu',
  templateUrl: './create-menu.component.html',
  styleUrls: ['./create-menu.component.scss']
})
export class CreateMenuComponent implements OnInit {
  @Output() creationMenuSuccess = new EventEmitter<void>();

  menu = { menuId: 0, menuName: '', menuStatus: 0 };
  menuOptions: any[] = [];

  pages: any[] = [];
  components: any[] = [];
  categories: any[] = [];
  collections: any[] = [];
  subCategories: any[] = [];
  subSubCategories: any[] = [];

  loading = false;
  itemListId = '';

  // Stepper
  currentStep = 1;
  readonly steps = [
    { index: 1, label: 'MENU_STEP_2_TITLE', description: 'MENU_STEP_2_DESC' },
    { index: 2, label: 'MENU_STEP_3_TITLE', description: 'MENU_STEP_3_DESC' },
    { index: 3, label: 'MENU_STEP_4_TITLE', description: 'MENU_STEP_4_DESC' },
  ];
  selectedOptionIndex = -1;
  activeSubOptionIndex = -1;
  private shouldReturnToOptionsAfterSave = false;

  // Template-accessible helpers
  isOptionType = isOptionType;
  isValidHttpUrl = isValidHttpUrl;
  getOptionTypeLabel = getOptionTypeLabel;

  constructor(
    private apiService: ApiService,
    public themingService: ThemingService,
    private lambdaService: LambdaService
  ) { }

  ngOnInit(): void {
    this.loading = true;
    Promise.all([
      this.getComponents(),
      this.getCollections(),
      this.getCategories(),
      this.getSubCategories(),
      this.getSubSubCategories(),
      this.getPages()
    ]).then(() => {
      this.loading = false;
    }).catch((error) => {
      console.error(error);
      this.loading = false;
    });
  }

  async getPages(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getPages(this.themingService.shopId);
      const pageRows = Array.isArray(response.data) ? response.data : [];
      this.pages = pageRows;
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      this.loading = false;
    }
  }

  async getCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCategories(this.themingService.shopId);
      const categoryRows = Array.isArray(response[0]) ? response[0] : [];
      this.categories = categoryRows;
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async getCollections(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCollections(this.themingService.shopId);
      this.collections = Array.isArray(response.collections) ? response.collections : [];
    } catch (error) {
      console.error('Error fetching collections:', error);
      this.collections = [];
    }
  }

  async getSubCategories(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getSubCategories(this.themingService.shopId);
      this.subCategories = Array.isArray(response.subCategories) ? response.subCategories : [];
    } catch (error) {
      console.error('Error fetching subCategories:', error);
    } finally {
      this.loading = false;
    }
  }

  async getSubSubCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getSubSubCategories(this.themingService.shopId);
      const subSubCategoryRows = Array.isArray(response.subSubCategories) ? response.subSubCategories : [];
      this.subSubCategories = subSubCategoryRows;
    } catch (error) {
      console.error('Error fetching subSubCategories:', error);
    }
  }

  async getComponents(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getComponents(this.themingService.shopId);
      const componentRows = Array.isArray(response[0]) ? response[0] : [];
      this.components = componentRows;
    } catch (error) {
      console.error('Error fetching components:', error);
    } finally {
      this.loading = false;
    }
  }

  // ─── Stepper ──────────────────────────────

  get stepProgress(): number {
    return (this.currentStep / this.steps.length) * 100;
  }

  resolveStep(targetStep: number): number {
    const boundedStep = Math.max(1, Math.min(targetStep, this.steps.length));

    if (boundedStep === 1) {
      return boundedStep;
    }

    if (this.menuOptions.length === 0) {
      return 1;
    }

    if (this.selectedOptionIndex < 0 || this.selectedOptionIndex >= this.menuOptions.length) {
      return 1;
    }

    return boundedStep;
  }

  nextStep(): void {
    this.currentStep = this.resolveStep(this.currentStep + 1);
  }

  prevStep(): void {
    this.currentStep = Math.max(this.currentStep - 1, 1);
  }

  goToStep(target: number): void {
    this.currentStep = this.resolveStep(target);
  }

  isCurrentStep(step: number): boolean { return this.currentStep === step; }

  isStepComplete(step: number): boolean {
    return step < this.currentStep && this.isStepValid(step);
  }

  isStepValid(step: number): boolean {
    switch (step) {
      case 1: return this.menuOptions.length > 0;
      case 2: {
        const opt = this.menuOptions[this.selectedOptionIndex];
        return opt ? isOptionValid(opt).valid : false;
      }
      case 3:
      default: return true;
    }
  }

  editOption(index: number): void {
    this.selectedOptionIndex = index;
    this.activeSubOptionIndex = -1;
    this.currentStep = this.resolveStep(2);
  }

  viewSuboptions(index: number): void {
    this.selectedOptionIndex = index;
    this.activeSubOptionIndex = -1;
    this.currentStep = this.resolveStep(3);
  }

  backToOptions(): void {
    this.selectedOptionIndex = -1;
    this.activeSubOptionIndex = -1;
    this.currentStep = 1;
  }

  backToOption(): void {
    this.activeSubOptionIndex = -1;
    this.currentStep = 2;
  }

  editSubOption(index: number): void {
    this.activeSubOptionIndex = index;
  }

  get selectedOption(): any {
    return this.menuOptions[this.selectedOptionIndex] ?? null;
  }

  get activeSubOption(): any {
    return this.selectedOption?.children?.[this.activeSubOptionIndex] ?? null;
  }

  trackByIndex(index: number): number { return index; }

  // ─── Option management ────────────────────

  addMenuOption(): void {
    this.menuOptions.push(createBlankOption(this.menuOptions.length + 1));
  }

  addSubMenuOption(parentIndex: number): void {
    const parent = this.menuOptions[parentIndex];
    parent.children.push(createBlankSubOption(parent.children.length + 1));
  }

  removeSubMenuOption(parentIndex: number, childIndex: number): void {
    const children = this.menuOptions[parentIndex].children;
    children.splice(childIndex, 1);
    children.forEach((c: any, i: number) => c.position = i + 1);

    if (this.selectedOptionIndex === parentIndex) {
      if (this.activeSubOptionIndex === childIndex) {
        this.activeSubOptionIndex = children.length ? Math.min(childIndex, children.length - 1) : -1;
      } else if (childIndex < this.activeSubOptionIndex) {
        this.activeSubOptionIndex -= 1;
      }
    }
  }

  removeMenuOption(index: number): void {
    const removedSelectedOption = this.selectedOptionIndex === index;
    this.menuOptions.splice(index, 1);
    this.menuOptions.forEach((opt, i) => opt.position = i + 1);

    if (removedSelectedOption) {
      this.backToOptions();
      return;
    }

    if (index < this.selectedOptionIndex) {
      this.selectedOptionIndex -= 1;
    }

    if (this.selectedOptionIndex >= this.menuOptions.length) {
      this.selectedOptionIndex = -1;
      this.activeSubOptionIndex = -1;
    }
  }

  onOptionChange(option: string, index: number): void {
    const target = this.menuOptions[index];
    target.categoryId = 0;
    target.subCategoryId = 0;
    target.subSubCategoryId = 0;
    target.collectionId = 0;
    target.optionUrl = '';
    target.componentId = 0;
    target.pageId = 0;
    target.optionType = OPTION_TYPE_MAP[option] || 0;
  }

  onSubOptionTypeChange(option: string): void {
    const child = this.activeSubOption;
    if (!child) return;
    child.categoryId = 0;
    child.subCategoryId = 0;
    child.subSubCategoryId = 0;
    child.collectionId = 0;
    child.optionUrl = '';
    child.componentId = 0;
    child.pageId = 0;
    child.optionType = OPTION_TYPE_MAP[option] || 0;
  }

  applySelectValue(target: any, field: string, event: any): void {
    target[field] = +event.target.value;
  }

  onPageChange(event: any, index: number) {
    const selectedPage = this.pages.find(page => page.pageId === +event.target.value);
    if (selectedPage) {
      this.menuOptions[index].pageId = selectedPage.pageId;
    }
  }

  onCategoryChange(event: any, index: number) {
    const selectedCategory = this.categories.find(category => category.categoryId === +event.target.value);
    if (selectedCategory) {
      this.menuOptions[index].categoryId = selectedCategory.categoryId;
    }
  }

  onSubCategoryChange(event: any, index: number) {
    const selectedSubCategory = this.subCategories.find(subCategory => subCategory.subCategoryId === +event.target.value);
    if (selectedSubCategory) {
      this.menuOptions[index].subCategoryId = selectedSubCategory.subCategoryId;
    }
  }

  onSubSubCategoryChange(event: any, index: number) {
    const selectedSubSubCategory = this.subSubCategories.find(subSubCategory => subSubCategory.subSubCategoryId === +event.target.value);
    if (selectedSubSubCategory) {
      this.menuOptions[index].subSubCategoryId = selectedSubSubCategory.subSubCategoryId;
    }
  }

  onCollectionChange(event: any, index: number) {
    const selectedCollection = this.collections.find(collection => collection.collectionId === +event.target.value);
    if (selectedCollection) {
      this.menuOptions[index].collectionId = selectedCollection.collectionId;
    }
  }

  onComponentChange(event: any, index: number) {
    const selectedComponent = this.components.find(component => component.componentId === +event.target.value);
    if (selectedComponent) {
      this.menuOptions[index].componentId = selectedComponent.componentId;
      if (selectedComponent.categoryId) {
        this.menuOptions[index].categoryId = selectedComponent.categoryId;
      }
      if (selectedComponent.subCategoryId) {
        this.menuOptions[index].subCategoryId = selectedComponent.subCategoryId;
      }
      if (selectedComponent.subSubCategoryId) {
        this.menuOptions[index].subSubCategoryId = selectedComponent.subSubCategoryId;
      }
      if (selectedComponent.collectionId) {
        this.menuOptions[index].collectionId = selectedComponent.collectionId;
      }
      if (selectedComponent.pageId) {
        this.menuOptions[index].pageId = selectedComponent.pageId;
      }
    }
  }

  onUrlChange(event: any, index: number) {
    this.menuOptions[index].optionUrl = event.target.value;
  }

  saveMenu(): void {
    this.shouldReturnToOptionsAfterSave = false;
    this.validateFormData();
  }

  saveAndBackFromOption(): void {
    this.shouldReturnToOptionsAfterSave = true;
    this.validateFormData();
  }

  validateFormData(): void {
    for (let i = 0; i < this.menuOptions.length; i++) {
      const result = isOptionValid(this.menuOptions[i]);
      if (!result.valid) {
        this.shouldReturnToOptionsAfterSave = false;
        this.selectedOptionIndex = i;
        this.activeSubOptionIndex = -1;
        this.currentStep = 2;
        if (result.errorType === 'url') {
          this.itemListId = `${i + 1}`;
          $('#errorCreateMenuUrl').modal('show');
        } else {
          $('#requiredMenuModal').modal('show');
        }
        return;
      }
      const children = this.menuOptions[i].children || [];
      for (let j = 0; j < children.length; j++) {
        const childResult = isOptionValid(children[j]);
        if (!childResult.valid) {
          this.shouldReturnToOptionsAfterSave = false;
          this.selectedOptionIndex = i;
          this.activeSubOptionIndex = j;
          this.currentStep = 3;
          if (childResult.errorType === 'url') {
            this.itemListId = `S${i + 1}.${j + 1}`;
            $('#errorCreateMenuUrl').modal('show');
          } else {
            $('#requiredMenuModal').modal('show');
          }
          return;
        }
      }
    }
    this.createMenu();
  }

  createMenu() {
    this.loading = true;
    const payload = {
      menuId: 0,
      menuName: this.menu.menuName,
      menuStatus: this.menu.menuStatus || 0,
      menuOptions: this.menuOptions
    };
    this.lambdaService.createMenu(
      this.themingService.shopId,
      payload
    )
    .then((data: any) => {
      if (data?.success) {
        const shouldReturnToOptionsAfterSave = this.shouldReturnToOptionsAfterSave;
        this.shouldReturnToOptionsAfterSave = false;
        this.loading = false;
        if (shouldReturnToOptionsAfterSave) {
          this.backToOptions();
        }
        $('#successMenuModal').modal('show');
        $('#successMenuModal')
          .off('hidden.bs.modal')
          .on('hidden.bs.modal', () => {
            this.resetForm();
            this.creationMenuSuccess.emit();
          });
      } else {
        this.shouldReturnToOptionsAfterSave = false;
        this.loading = false;
        $('#errorMenuModal').modal('show');
      }
    })
    .catch(err => {
      console.error('createMenu error:', err);
      this.shouldReturnToOptionsAfterSave = false;
      this.loading = false;
      $('#errorMenuModal').modal('show');
    });
  }

  resetForm(): void {
    this.menu = { menuId: 0, menuName: '', menuStatus: 0 };
    this.menuOptions = [];
    this.currentStep = 1;
    this.selectedOptionIndex = -1;
    this.activeSubOptionIndex = -1;
  }

  closeModal(): void {
    $('#successMenuModal').modal('hide');
    $('#errorMenuModal').modal('hide');
    $('#requiredMenuModal').modal('hide');
    $('#errorCreateMenuUrl').modal('hide');
  }
}
